<template>
  <div>
    <v-menu
      v-model="menu"
      :close-on-content-click="false"
      offset-y
      left
      min-width="280"
      max-width="calc(100vw - 24px)"
    >
      <template v-slot:activator="{on, attrs}">
        <v-btn small text color="primary" class="px-1" v-bind="attrs" v-on="on">
          <v-icon small left>mdi-text-box-plus-outline</v-icon>
          {{ $t('ReserveCommonNotes.useCommonNote') }}
        </v-btn>
      </template>

      <v-card class="common-note-card">
        <v-card-title class="text-subtitle-1 py-2">
          {{ $t('ReserveCommonNotes.pickerTitle') }}
        </v-card-title>
        <v-progress-linear v-if="loading" indeterminate color="primary"/>

        <div class="common-note-list">
          <v-list v-if="notes.length" dense class="py-0">
            <v-list-item v-for="item in notes" :key="item.id || item.content" @click="selectNote(item.content)">
              <v-list-item-content class="common-note-item-content">
                <v-list-item-title
                  class="common-note-content"
                  @mouseenter="startMarquee"
                  @mouseleave="stopMarquee"
                >
                  <span class="common-note-content-text">{{ item.content }}</span>
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-icon>
                <v-icon small color="primary">mdi-plus</v-icon>
              </v-list-item-icon>
            </v-list-item>
          </v-list>
          <div v-else-if="!loading" class="pa-4 text-body-2 grey--text text--darken-1 text-center">
            {{ loadFailed ? $t('ReserveCommonNotes.loadFailed') : $t('ReserveCommonNotes.empty') }}
          </div>
        </div>

        <v-divider/>
        <v-card-actions>
          <v-btn v-if="loadFailed" small text @click="loadNotes">
            {{ $t('ReserveCommonNotes.retry') }}
          </v-btn>
          <v-spacer/>
          <v-btn small text color="primary" @click="openSettings">
            {{ $t('ReserveCommonNotes.openSettings') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>

    <reserve-common-note-settings-dialog v-model="settingsDialog" @saved="handleSaved"/>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import {getReserveNoteTemplatesAPI} from '@/api'
import {appendCommonNote} from '@/utils/reserveCommonNotes'
import ReserveCommonNoteSettingsDialog from './ReserveCommonNoteSettingsDialog'

export default {
  name: 'ReserveCommonNotePicker',
  components: {ReserveCommonNoteSettingsDialog},
  props: {
    value: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      menu: false,
      settingsDialog: false,
      notes: [],
      loading: false,
      loadFailed: false,
      loadRequestId: 0,
    }
  },
  computed: {
    ...mapGetters(['axiosParamData']),
  },
  watch: {
    menu(isOpen) {
      if (isOpen) this.loadNotes()
      else this.loadRequestId += 1
    },
  },
  methods: {
    extractItems(response) {
      const payload = response && response.data
      if (payload && Array.isArray(payload.data)) return payload.data
      return Array.isArray(payload) ? payload : []
    },
    loadNotes() {
      const requestId = ++this.loadRequestId
      this.loading = true
      this.loadFailed = false
      getReserveNoteTemplatesAPI(this.axiosParamData)
        .then(response => {
          if (requestId !== this.loadRequestId) return
          this.notes = this.extractItems(response).filter(item => item.content)
        })
        .catch(() => {
          if (requestId !== this.loadRequestId) return
          this.loadFailed = true
        })
        .finally(() => {
          if (requestId !== this.loadRequestId) return
          this.loading = false
        })
    },
    selectNote(content) {
      this.$emit('input', appendCommonNote(this.value, content))
    },
    startMarquee(event) {
      const container = event.currentTarget
      const content = container.querySelector('.common-note-content-text')
      const distance = content ? content.scrollWidth - container.clientWidth : 0
      if (distance <= 0) return

      container.style.setProperty('--marquee-distance', `-${distance}px`)
      container.style.setProperty('--marquee-duration', `${Math.min(14, Math.max(3, distance / 60))}s`)
      container.classList.add('is-marquee')
    },
    stopMarquee(event) {
      const container = event.currentTarget
      container.classList.remove('is-marquee')
      container.style.removeProperty('--marquee-distance')
      container.style.removeProperty('--marquee-duration')
    },
    openSettings() {
      this.menu = false
      this.settingsDialog = true
    },
    handleSaved(items) {
      this.notes = items.filter(item => item.content)
      this.$emit('settings-saved', this.notes)
    },
  },
}
</script>

<style scoped>
.common-note-card {
  width: 420px;
  max-width: calc(100vw - 24px);
}

.common-note-list {
  max-height: 260px;
  overflow-y: auto;
}

.common-note-item-content {
  min-width: 0;
}

.common-note-content {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.common-note-content-text {
  display: inline-block;
  white-space: nowrap;
}

.common-note-content.is-marquee {
  text-overflow: clip;
}

.common-note-content.is-marquee .common-note-content-text {
  animation: common-note-marquee var(--marquee-duration) linear .35s infinite alternate;
}

@keyframes common-note-marquee {
  from {
    transform: translateX(0);
  }

  to {
    transform: translateX(var(--marquee-distance));
  }
}

@media (prefers-reduced-motion: reduce) {
  .common-note-content.is-marquee .common-note-content-text {
    animation: none;
  }
}
</style>
